import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSessionValid: boolean;
  refreshSession: () => Promise<void>;
  signUp: (email: string, password: string, firstName: string, invitationToken?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: (invitationToken?: string) => Promise<{ error: any }>;
  signInWithApple: (invitationToken?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSessionValid, setIsSessionValid] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;
    let refreshInterval: number | null = null;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('🔐 Auth event:', event, {
          userId: session?.user?.id,
          email: session?.user?.email,
          hasSession: !!session
        });
        
        // Synchroniser l'email dans profiles après confirmation
        if (event === 'USER_UPDATED' && session?.user) {
          setTimeout(async () => {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('email')
                .eq('user_id', session.user.id)
                .single();
              
              // Synchroniser seulement si l'email a changé
              if (profile && profile.email !== session.user.email) {
                await supabase
                  .from('profiles')
                  .update({ email: session.user.email })
                  .eq('user_id', session.user.id);
                
                toast({
                  title: "Email confirmé",
                  description: "Votre email a été changé avec succès.",
                });
              }
            } catch (error) {
              console.error('Error syncing email to profiles:', error);
            }
          }, 0);
        }
        
        // Mettre à jour l'état utilisateur immédiatement quand Supabase confirme le changement
        if (event === 'USER_UPDATED') {
          setSession(session);
          setUser(session?.user ?? null);
          setIsSessionValid(!!session);
        }
        
        // Gestion intelligente des événements d'auth
        if (event === 'SIGNED_OUT') {
          setSession(session);
          setUser(session?.user ?? null);
          setIsSessionValid(false);
        } else if (event === 'TOKEN_REFRESHED') {
          setSession(session);
          setUser(session?.user ?? null);
          setIsSessionValid(!!session);
        } else if (event === 'INITIAL_SESSION') {
          setSession(session);
          setUser(session?.user ?? null);
          setIsSessionValid(!!session);
          if (session?.user && !(window as any).__oauthDispatched) {
            console.log('🚦 INITIAL_SESSION → dispatch oauth:signedin');
            (window as any).__oauthDispatched = true;
            window.dispatchEvent(new CustomEvent('oauth:signedin', {
              detail: { userId: session.user.id }
            }));
          }
        } else if (event === 'SIGNED_IN') {
          setSession(session);
          setUser(session?.user ?? null);
          setIsSessionValid(!!session);
          
          // Dispatcher l'événement pour navigation immédiate (dédoublonné)
          if (session?.user && !(window as any).__oauthDispatched) {
            console.log('🚦 SIGNED_IN event → dispatch oauth:signedin');
            (window as any).__oauthDispatched = true;
            window.dispatchEvent(new CustomEvent('oauth:signedin', {
              detail: { userId: session.user.id }
            }));
          }
          
          // Démarrer un rafraîchissement proactif de session
          if (session && !refreshInterval) {
            refreshInterval = window.setInterval(async () => {
              try {
                const { data, error } = await supabase.auth.refreshSession();
                if (error) {
                  console.warn('Erreur de rafraîchissement de session:', error.message);
                }
              } catch (err) {
                console.warn('Erreur lors du rafraîchissement:', err);
              }
            }, 15 * 60 * 1000); // Rafraîchir toutes les 15 minutes
          }
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session with retry logic
    const checkSession = async (retries = 3) => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!mounted) return;
        
        if (error) {
          console.error('Error getting session:', error);
          // Retry si ce n'est pas un problème d'auth
          if (retries > 0 && error.message !== 'Invalid JWT') {
            setTimeout(() => checkSession(retries - 1), 1000);
            return;
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsSessionValid(!!session);
        setLoading(false);
        
        // Dispatch oauth:signedin on cold start if not already dispatched
        if (session?.user && !(window as any).__oauthDispatched) {
          console.log('🚦 checkSession → dispatch oauth:signedin');
          (window as any).__oauthDispatched = true;
          window.dispatchEvent(new CustomEvent('oauth:signedin', {
            detail: { userId: session.user.id }
          }));
        }
        
        // Démarrer le rafraîchissement si on a une session
        if (session && !refreshInterval) {
          refreshInterval = window.setInterval(async () => {
            try {
              await supabase.auth.refreshSession();
            } catch (err) {
              console.warn('Erreur lors du rafraîchissement automatique:', err);
            }
          }, 15 * 60 * 1000);
        }
      } catch (error) {
        if (!mounted) return;
        console.error('Error getting session:', error);
        if (retries > 0) {
          setTimeout(() => checkSession(retries - 1), 1000);
        } else {
          setLoading(false);
        }
      }
    };

    checkSession();

    // Safety timeout to ensure loading ends
    const timeoutId = setTimeout(() => {
      if (mounted) {
        setLoading(false);
      }
    }, 5000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (refreshInterval) {
        window.clearInterval(refreshInterval);
      }
      clearTimeout(timeoutId);
    };
  }, []);

  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
        setIsSessionValid(false);
        throw error;
      }
      setIsSessionValid(!!data.session);
    } catch (error) {
      setIsSessionValid(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, firstName: string, invitationToken?: string) => {
    // Use a stable production URL for email redirects (works on native & web)
    const baseUrl = 'https://get-clan.com';
    const redirectUrl = invitationToken 
      ? `${baseUrl}/auth?invitation=${invitationToken}&email=${encodeURIComponent(email)}`
      : `${baseUrl}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          invitation_token: invitationToken
        }
      }
    });
    
    if (error) {
      toast({
        title: "Erreur d'inscription",
        description: error.message,
        variant: "destructive",
      });
    } else if (invitationToken) {
      // For invitations, try immediate sign in after signup
      setTimeout(async () => {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (signInError) {
          // If immediate sign-in fails, show confirmation message
          toast({
            title: "Vérifiez votre email",
            description: "Un email de confirmation a été envoyé. Cliquez sur le lien dans l'email pour activer votre compte.",
          });
        }
      }, 1000);
    } else {
      // For regular signup, immediately try to sign in to allow dashboard access
      setTimeout(async () => {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        // Don't show error toast here - user will see the email confirmation banner instead
      }, 500);
    }
    
    return { error };
  };

  const processInvitation = async (token: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('confirm-invitation', {
        body: { token }
      });

      if (error) {
        console.error('Error confirming invitation:', error);
        toast({
          title: 'Invitation',
          description: "Impossible d'accepter l'invitation. Le lien a peut-être expiré.",
          variant: 'destructive',
        });
        return;
      }

      // Silent invitation confirmation - no toast
    } catch (err: any) {
      console.error('Error invoking confirm-invitation:', err);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast({
        title: "Erreur de connexion",
        description: error.message,
        variant: "destructive",
      });
    }
    
    return { error };
  };

  const signInWithGoogle = async (invitationToken?: string) => {
    const isNative = Capacitor.isNativePlatform();
    const baseRedirect = isNative
      ? 'clan://auth/callback'
      : `${window.location.origin}/auth`;
    const redirect = `${baseRedirect}${invitationToken ? `?invitation=${encodeURIComponent(invitationToken)}` : ''}`;

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirect,
          skipBrowserRedirect: isNative,
          scopes: 'email profile',
          queryParams: invitationToken ? { invitation: invitationToken } : undefined,
        },
      });
      if (error) throw error;

      if (isNative && data?.url) {
        await Browser.open({ url: data.url });
      } else if (data?.url) {
        window.location.href = data.url;
      }
      return { error: null };
    } catch (error: any) {
      toast({
        title: 'Erreur de connexion Google',
        description: error?.message || 'Connexion Google impossible.',
        variant: 'destructive',
      });
      return { error };
    }
  };

  const signInWithApple = async (invitationToken?: string) => {
    const isNative = Capacitor.isNativePlatform();
    const baseRedirect = isNative
      ? 'clan://auth/callback'
      : `${window.location.origin}/auth`;
    const redirect = `${baseRedirect}${invitationToken ? `?invitation=${encodeURIComponent(invitationToken)}` : ''}`;

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirect,
          skipBrowserRedirect: isNative,
          queryParams: invitationToken ? { invitation: invitationToken } : undefined,
        },
      });
      if (error) throw error;

      if (isNative && data?.url) {
        await Browser.open({ url: data.url });
      } else if (data?.url) {
        window.location.href = data.url;
      }
      return { error: null };
    } catch (error: any) {
      toast({
        title: 'Erreur de connexion Apple',
        description: error?.message || 'Connexion Apple impossible.',
        variant: 'destructive',
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Toujours supprimer les tokens locaux en premier
      await supabase.auth.signOut({ scope: 'local' });
      // Essayer de révoquer côté serveur, ignorer les erreurs bénignes
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        const msg = (error.message || '').toLowerCase();
        const code = (error as any).code || (error as any).error_code;
        const status = (error as any).status;
        const benign = code === 'session_not_found' 
          || msg.includes('session not found') 
          || msg.includes('auth session missing') 
          || msg.includes('session missing') 
          || status === 403;
        if (!benign) {
          toast({
            title: "Erreur de déconnexion",
            description: error.message,
            variant: "destructive",
          });
          return;
        }
      }
    } catch (error: any) {
      // Même en cas d'erreur réseau, on nettoie le client
      console.error('Erreur lors de la déconnexion:', error);
    }
    
    // Toujours nettoyer le côté client et rediriger
    setSession(null);
    setUser(null);
    setIsSessionValid(false);
    try {
      // Filet de sécurité: purge explicite des tokens Supabase
      localStorage.removeItem('sb-wqagrmomwzikqpxwiskl-auth-token');
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('sb-wqagrmomwzikqpxwiskl-auth-token')) localStorage.removeItem(k);
      });
    } catch {}
    // Reset flags de navigation oauth éventuels
    try { delete (window as any).__oauthDispatched; } catch {}
    
    // Rediriger vers la page d'authentification via SPA navigation
    window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path: '/auth', replace: true } }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      isSessionValid,
      refreshSession,
      signUp,
      signIn,
      signInWithGoogle,
      signInWithApple,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}